import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Grid,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  RadioButtonChecked as ActiveIcon,
  RadioButtonUnchecked as InactiveIcon,
  Settings as SettingsIcon,
  Cloud as CloudIcon,
  Computer as ComputerIcon
} from '@mui/icons-material';
import apiClient from '../../services/apiClient';
import ProfileCustomDialog from '../ProfileCustomDialog';
import ProfileEditDialog from '../ProfileEditDialog';

interface Profile {
  id: string;
  name: string;
  description: string;
  environment: string;
  isActive: boolean;
  hasCredentials: boolean;
  isCustom?: boolean;
  createdAt?: string;
}

interface TestResult {
  profileId: string;
  name: string;
  environment: string;
  status: 'success' | 'failed';
  connectionTime?: number;
  error?: string;
}

const ProfilesPage: React.FC = () => {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [showCustomDialog, setShowCustomDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingProfile, setEditingProfile] = useState<Profile | null>(null);
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [testingAll, setTestingAll] = useState(false);
  const [switchingProfile, setSwitchingProfile] = useState<string | null>(null);
  const [activeProfile, setActiveProfile] = useState<string>('');

  const loadProfiles = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await apiClient.get('/recup-profiles');
      
      if (response.data.success) {
        setProfiles(response.data.data.profiles);
        setActiveProfile(response.data.data.activeProfile.id);
      } else {
        setError(response.data.error || 'Errore durante il caricamento');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Errore di connessione');
    } finally {
      setLoading(false);
    }
  };

  const switchProfile = async (profileId: string) => {
    if (switchingProfile === profileId || profileId === activeProfile) return;

    try {
      setSwitchingProfile(profileId);
      
      const response = await apiClient.post('/recup-profiles/switch', { profileId });
      
      if (response.data.success) {
        setActiveProfile(profileId);
        // Aggiorna la lista per mostrare il nuovo stato
        await loadProfiles();
      } else {
        setError(response.data.error || 'Errore durante il cambio profilo');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Errore durante il cambio');
    } finally {
      setSwitchingProfile(null);
    }
  };

  const testConnection = async (profileId: string) => {
    // Per ora testiamo solo il profilo attivo
    if (profileId !== activeProfile) {
      alert('Per testare la connessione, prima attiva il profilo');
      return;
    }

    try {
      const response = await apiClient.get('/recup-profiles/test-connection');
      
      const result: TestResult = {
        profileId,
        name: profiles.find(p => p.id === profileId)?.name || '',
        environment: profiles.find(p => p.id === profileId)?.environment || '',
        status: response.data.success ? 'success' : 'failed',
        connectionTime: response.data.data?.connectionTime,
        error: response.data.error
      };

      setTestResults(prev => prev.filter(r => r.profileId !== profileId).concat(result));
    } catch (err: any) {
      const result: TestResult = {
        profileId,
        name: profiles.find(p => p.id === profileId)?.name || '',
        environment: profiles.find(p => p.id === profileId)?.environment || '',
        status: 'failed',
        error: err.response?.data?.error || err.message
      };

      setTestResults(prev => prev.filter(r => r.profileId !== profileId).concat(result));
    }
  };

  const handleEditProfile = (profile: Profile) => {
    setEditingProfile(profile);
    setShowEditDialog(true);
  };

  const handleEditDialogClose = () => {
    setShowEditDialog(false);
    setEditingProfile(null);
  };

  const handleDeleteProfile = async (profile: Profile) => {
    if (!profile.isCustom) {
      alert('Non è possibile eliminare i profili predefiniti');
      return;
    }

    if (!confirm(`Sei sicuro di voler eliminare il profilo "${profile.name}"?`)) {
      return;
    }

    try {
      const response = await apiClient.delete(`/recup-profiles/custom/${profile.id}`);
      
      if (response.data.success) {
        await loadProfiles(); // Ricarica la lista
      } else {
        setError(response.data.error || 'Errore durante l\'eliminazione');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Errore durante l\'eliminazione');
    }
  };

  const getEnvironmentIcon = (environment: string) => {
    switch (environment.toLowerCase()) {
      case 'production':
        return <CloudIcon />;
      case 'custom':
        return <SettingsIcon />;
      default:
        return <ComputerIcon />;
    }
  };

  const getEnvironmentColor = (environment: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (environment.toLowerCase()) {
      case 'production':
        return 'error';
      case 'staging':
        return 'warning';
      case 'test':
        return 'info';
      case 'custom':
        return 'secondary';
      default:
        return 'primary';
    }
  };

  const getTestResult = (profileId: string) => {
    return testResults.find(r => r.profileId === profileId);
  };

  useEffect(() => {
    loadProfiles();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Gestione Profili RECUP
        </Typography>
        
        <Box>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadProfiles}
            disabled={loading}
            sx={{ mr: 1 }}
          >
            Ricarica
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowCustomDialog(true)}
          >
            Nuovo Profilo
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Gestisci i profili di connessione per diversi ambienti RECUP. 
        Il profilo attivo determina quale endpoint verrà utilizzato per tutte le operazioni.
      </Typography>

      <Grid container spacing={3}>
        {profiles.map((profile) => {
          const testResult = getTestResult(profile.id);
          const isActive = profile.id === activeProfile;
          const isSwitching = switchingProfile === profile.id;

          return (
            <Grid item xs={12} md={6} lg={4} key={profile.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  border: isActive ? '2px solid' : '1px solid',
                  borderColor: isActive ? 'primary.main' : 'divider'
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getEnvironmentIcon(profile.environment)}
                      <Typography variant="h6" component="h3">
                        {profile.name}
                      </Typography>
                    </Box>
                    
                    {isActive ? (
                      <ActiveIcon color="primary" />
                    ) : (
                      <InactiveIcon color="disabled" />
                    )}
                  </Box>

                  {profile.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {profile.description}
                    </Typography>
                  )}

                  <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                    <Chip 
                      label={profile.environment}
                      size="small"
                      color={getEnvironmentColor(profile.environment)}
                      variant="outlined"
                    />
                    {profile.isCustom && (
                      <Chip 
                        label="Custom"
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                    )}
                    {profile.hasCredentials ? (
                      <Chip 
                        label="Credenziali OK"
                        size="small"
                        color="success"
                        variant="outlined"
                        icon={<CheckIcon />}
                      />
                    ) : (
                      <Chip 
                        label="Credenziali mancanti"
                        size="small"
                        color="error"
                        variant="outlined"
                        icon={<ErrorIcon />}
                      />
                    )}
                  </Box>

                  {testResult && (
                    <Box sx={{ mt: 2 }}>
                      <Divider sx={{ mb: 1 }} />
                      <Typography variant="caption" display="block">
                        Ultimo test:
                      </Typography>
                      {testResult.status === 'success' ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <CheckIcon color="success" fontSize="small" />
                          <Typography variant="body2" color="success.main">
                            Connessione riuscita ({testResult.connectionTime}ms)
                          </Typography>
                        </Box>
                      ) : (
                        <Box display="flex" alignItems="center" gap={1}>
                          <ErrorIcon color="error" fontSize="small" />
                          <Typography variant="body2" color="error.main">
                            Errore: {testResult.error}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}

                  {profile.createdAt && (
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      Creato: {new Date(profile.createdAt).toLocaleDateString()}
                    </Typography>
                  )}
                </CardContent>

                <CardActions>
                  {!isActive && profile.hasCredentials && (
                    <Button
                      size="small"
                      onClick={() => switchProfile(profile.id)}
                      disabled={isSwitching}
                      startIcon={isSwitching ? <CircularProgress size={14} /> : <ActiveIcon />}
                    >
                      {isSwitching ? 'Attivazione...' : 'Attiva'}
                    </Button>
                  )}
                  
                  <Button
                    size="small"
                    onClick={() => handleEditProfile(profile)}
                    startIcon={<EditIcon />}
                  >
                    Modifica
                  </Button>

                  {profile.isCustom && (
                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleDeleteProfile(profile)}
                      startIcon={<DeleteIcon />}
                    >
                      Elimina
                    </Button>
                  )}

                  {isActive && (
                    <Button
                      size="small"
                      onClick={() => testConnection(profile.id)}
                      startIcon={<RefreshIcon />}
                    >
                      Test
                    </Button>
                  )}

                  {isActive && (
                    <Chip 
                      label="ATTIVO"
                      size="small"
                      color="primary"
                    />
                  )}
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <ProfileCustomDialog
        open={showCustomDialog}
        onClose={() => setShowCustomDialog(false)}
        onProfileCreated={loadProfiles}
      />

      <ProfileEditDialog
        open={showEditDialog}
        profile={editingProfile}
        onClose={handleEditDialogClose}
        onProfileUpdated={loadProfiles}
      />
    </Box>
  );
};

export default ProfilesPage;